import BackendService from 'BackendService';
import { backendErrorMessage } from '../../../services/baseBackendService/structuredErrors';
import { useCallback, useRef, useState } from 'react';
import AIChatMessage from '../../../models/AIChatMessage';
import { CurrentData, UploadedDocument, UploadProgress } from './types';
import {
  generateTempId,
  getUploadFileName,
  loadUploadedDocuments,
  saveUploadedDocuments,
} from './utils';

interface UseFundOpportunityDocumentUploadOptions {
  setMessages: React.Dispatch<React.SetStateAction<AIChatMessage[]>>;
  setCurrentData: React.Dispatch<React.SetStateAction<CurrentData | undefined>>;
  messagesRef: React.MutableRefObject<AIChatMessage[]>;
  currentDataRef: React.MutableRefObject<CurrentData | undefined>;
}

export function useFundOpportunityDocumentUpload({
  setMessages,
  setCurrentData,
  messagesRef,
  currentDataRef,
}: UseFundOpportunityDocumentUploadOptions) {
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Map<string, UploadProgress>>(new Map());
  const [hasUnprocessedDocuments, setHasUnprocessedDocuments] = useState(false);

  const uploadOrderCounterRef = useRef(0);

  const getOrCreateTempFundOpportunityId = useCallback((): string => {
    if (currentDataRef.current?.tempFundOpportunityId) {
      return currentDataRef.current.tempFundOpportunityId;
    }

    const newId = generateTempId();
    setCurrentData(
      (previousData) =>
        ({
          ...(previousData || {}),
          tempFundOpportunityId: newId,
        }) as CurrentData,
    );
    return newId;
  }, [currentDataRef, setCurrentData]);

  const processUpload = useCallback(
    async ({
      file,
      tempFundOpportunityId,
    }: {
      file: File;
      tempFundOpportunityId: string;
    }): Promise<void> => {
      const backendService = new BackendService();
      const fileName = getUploadFileName(file);

      setUploadProgress((previousProgress) => {
        const uploadingProgress = new Map(previousProgress);
        const existing = previousProgress.get(fileName);
        uploadingProgress.set(fileName, {
          fileName,
          progress: 0,
          status: 'uploading',
          file,
          order: existing?.order ?? uploadOrderCounterRef.current++,
        });
        return uploadingProgress;
      });

      try {
        const uploadUrlData = await backendService.getUploadUrl(
          tempFundOpportunityId,
          fileName,
          file.type,
          file.size,
        );

        await backendService.uploadToGCS(uploadUrlData.uploadUrl, file, (progress) => {
          setUploadProgress((previousProgress) => {
            const updatedProgress = new Map(previousProgress);
            const existing = previousProgress.get(fileName);
            updatedProgress.set(fileName, {
              fileName,
              progress,
              status: 'uploading',
              file,
              order: existing?.order ?? uploadOrderCounterRef.current++,
            });
            return updatedProgress;
          });
        });

        await backendService.completeUpload(
          tempFundOpportunityId,
          uploadUrlData.documentId,
          uploadUrlData.gcsPath,
        );

        const uploadedDoc: UploadedDocument = {
          id: uploadUrlData.documentId,
          name: fileName,
          timestamp: new Date().toISOString(),
          gcsPath: uploadUrlData.gcsPath,
        };

        const existingDocs = loadUploadedDocuments(tempFundOpportunityId);
        saveUploadedDocuments(tempFundOpportunityId, [...existingDocs, uploadedDoc]);

        setUploadProgress((previousProgress) => {
          const uploadedProgressMap = new Map(previousProgress);
          const existing = previousProgress.get(fileName);
          uploadedProgressMap.set(fileName, {
            fileName,
            progress: 100,
            status: 'completed',
            file,
            documentId: uploadUrlData.documentId,
            order: existing?.order ?? uploadOrderCounterRef.current++,
          });
          return uploadedProgressMap;
        });

        setUploadedDocuments((previousDocuments) => [...previousDocuments, uploadedDoc]);
        setHasUnprocessedDocuments(true);
      } catch (error) {
        console.error(`Error uploading ${fileName}:`, error);

        setUploadProgress((previousProgress) => {
          const errorProgress = new Map(previousProgress);
          const existing = previousProgress.get(fileName);
          errorProgress.set(fileName, {
            fileName,
            progress: 0,
            status: 'error',
            file,
            order: existing?.order ?? uploadOrderCounterRef.current++,
          });
          return errorProgress;
        });
        throw error;
      }
    },
    [],
  );

  const handleDocumentUpload = useCallback(
    async (files: File[]): Promise<void> => {
      if (files.length === 0) {
        return;
      }

      const tempFundOpportunityId = getOrCreateTempFundOpportunityId();

      setUploadProgress((previousProgress) => {
        const newUploadProgress = new Map(previousProgress);
        files.forEach((file) => {
          const fileName = getUploadFileName(file);
          newUploadProgress.set(fileName, {
            fileName,
            progress: 0,
            status: 'pending',
            file,
            order: uploadOrderCounterRef.current++,
          });
        });
        return newUploadProgress;
      });

      setIsUploadingFile(true);

      const fileNames = files.map((file) => getUploadFileName(file)).join(', ');
      const uploadStartMessage: AIChatMessage = {
        parts: [
          {
            text:
              files.length === 1
                ? `Uploading document: ${fileNames}`
                : `Uploading ${files.length} documents: ${fileNames}`,
          },
        ],
        role: 'user',
        timestamp: new Date(),
      };

      setMessages((previousMessages) => [...previousMessages, uploadStartMessage]);

      const uploadPromises = files.map((file) =>
        processUpload({
          file,
          tempFundOpportunityId,
        }),
      );

      Promise.allSettled(uploadPromises).then((results) => {
        const completedCount = results.filter((result) => result.status === 'fulfilled').length;
        const errorCount = results.filter((result) => result.status === 'rejected').length;
        const updatedMessages = [...messagesRef.current];

        if (completedCount > 0) {
          const summaryText =
            completedCount === 1
              ? `✅ Successfully uploaded ${completedCount} document. Click "Process Documents" to analyze.`
              : `✅ Successfully uploaded ${completedCount} documents. Click "Process Documents" to analyze.`;

          updatedMessages.push({
            role: 'model',
            parts: [{ text: summaryText }],
            timestamp: new Date(),
          });
        }

        if (errorCount > 0) {
          const errorFiles = results
            .map((result, index) =>
              result.status === 'rejected'
                ? `${getUploadFileName(files[index])}: ${backendErrorMessage(result.reason, 'Upload failed. Please try again.')}`
                : null,
            )
            .filter((message): message is string => message !== null);

          updatedMessages.push({
            role: 'model',
            parts: [
              {
                text: `⚠️ Failed to upload ${errorCount} document(s): ${errorFiles.join(', ')}`,
              },
            ],
            timestamp: new Date(),
          });
        }

        setMessages(updatedMessages);
        setIsUploadingFile(false);
      });
    },
    [getOrCreateTempFundOpportunityId, messagesRef, processUpload, setMessages],
  );

  const handleRemoveDocument = useCallback(
    (fileName: string): void => {
      setUploadProgress((previousProgress) => {
        const updatedProgress = new Map(previousProgress);
        updatedProgress.delete(fileName);
        return updatedProgress;
      });

      setUploadedDocuments((previousDocuments) => {
        const updatedDocs = previousDocuments.filter((document) => document.name !== fileName);
        const tempFundOpportunityId = getOrCreateTempFundOpportunityId();
        saveUploadedDocuments(tempFundOpportunityId, updatedDocs);
        setHasUnprocessedDocuments(updatedDocs.length > 0);
        return updatedDocs;
      });
    },
    [getOrCreateTempFundOpportunityId],
  );

  return {
    isUploadingFile,
    uploadedDocuments,
    uploadProgress,
    hasUnprocessedDocuments,
    getOrCreateTempFundOpportunityId,
    handleDocumentUpload,
    handleRemoveDocument,
    setHasUnprocessedDocuments,
  };
}
